import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Cpu } from 'lucide-react'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getItems } from '../../services'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import LinkedComponentsTable from './LinkedComponentsTable'
import ReturnToWarehouseDialog from './ReturnToWarehouseDialog'

interface ReturnTarget {
    ids: string[]
    label: string
}

const EquipmentComponents = () => {
    const { id } = useParams<{ id: string }>()
    const [returnTarget, setReturnTarget] = useState<ReturnTarget | null>(null)

    const { data: componentsData, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.WAREHOUSE_ITEMS, 'equipment-components', id],
        queryFn: () => getItems({ parent_id: id, item_type: 'component' }),
    })

    const components = componentsData?.results || []

    if (renderState()) return renderState()

    return (
        <Card className="mt-2">
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm">
                    <Cpu className="h-4 w-4" />
                    Linked Components
                    {components.length > 0 && (
                        <Badge variant="outline" className="ml-auto">
                            {components.length}
                        </Badge>
                    )}
                </CardTitle>
            </CardHeader>
            <CardContent>
                {components.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                        No components installed in this equipment
                    </p>
                ) : (
                    <LinkedComponentsTable
                        components={components}
                        onReturn={(ids, label) =>
                            setReturnTarget({ ids, label })
                        }
                    />
                )}
            </CardContent>

            <ReturnToWarehouseDialog
                open={returnTarget !== null}
                onOpenChange={(open) => !open && setReturnTarget(null)}
                componentIds={returnTarget?.ids || []}
                componentLabel={returnTarget?.label || ''}
            />
        </Card>
    )
}

export default EquipmentComponents
